import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { TravelerCaptureSource } from '@bandido/shared/types';
import { useCaptureObjectUrl, useCaptureSourceDetail } from './source-media';
import { CapturePdfPreview } from './CapturePdfPreview';

/** Shared authenticated viewer for the source dialog and the review's side panel. */
export const CaptureSourceContent = ({
  caseId,
  source,
  embedded = false,
}: {
  caseId: number;
  source: TravelerCaptureSource;
  embedded?: boolean;
}) => {
  const { t } = useTranslation();
  const detail = useCaptureSourceDetail(caseId, source);
  const fileUrl = useCaptureObjectUrl(detail.data?.blob);
  const [rotation, setRotation] = useState(0);
  const [imageFailed, setImageFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const viewport = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 1, height: 1 });
  useEffect(() => {
    if (!viewport.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(viewport.current);
    return () => observer.disconnect();
  }, [fileUrl]);
  return (
    <Box sx={{ minWidth: 0, ...(embedded ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}) }}>
      {!embedded && (
        <Typography variant="caption">
          {source.channel} · {new Date(source.occurredAt).toLocaleString()}
        </Typography>
      )}
      {detail.isLoading && <CircularProgress />}
      {detail.isError && <Alert severity="error">{t('copilot.capture.sourceError')}</Alert>}
      {detail.data?.truncated && <Alert severity="warning">{t('copilot.capture.truncated')}</Alert>}
      {detail.data?.text && (
        <Typography sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 2, overflowY: 'auto' }}>
          {detail.data.text}
        </Typography>
      )}
      {fileUrl &&
        (source.mimetype === 'application/pdf' && detail.data?.blob ? (
          <Box sx={embedded ? { flex: 1, minHeight: 0 } : {}}>
            <CapturePdfPreview blob={detail.data.blob} label={source.label} fit={embedded} />
          </Box>
        ) : source.mimetype?.toLowerCase().startsWith('image/') ? (
          <>
            <Stack direction="row" alignItems="center" gap={1} my={1} flexWrap="wrap" sx={{ flexShrink: 0 }}>
              <Button size="small" onClick={() => setRotation((value) => (value + 90) % 360)}>
                {t('copilot.capture.rotate')}
              </Button>
              <Button
                size="small"
                disabled={zoom <= 1}
                onClick={() => setZoom((value) => Math.max(1, value - 0.5))}
                aria-label={t('copilot.capture.zoomOut')}
              >
                −
              </Button>
              <Typography variant="caption">{Math.round(zoom * 100)}%</Typography>
              <Button
                size="small"
                disabled={zoom >= 3}
                onClick={() => setZoom((value) => Math.min(3, value + 0.5))}
                aria-label={t('copilot.capture.zoomIn')}
              >
                +
              </Button>
            </Stack>
            {imageFailed && <Alert severity="warning">{t('copilot.capture.previewError')}</Alert>}
            <Box
              ref={viewport}
              sx={{
                ...(embedded ? { flex: 1, minHeight: 0 } : { height: '52vh' }),
                overflow: 'auto',
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              <Box sx={{ width: `${zoom * 100}%`, height: `${zoom * 100}%`, position: 'relative' }}>
                <Box
                  component="img"
                  src={fileUrl}
                  alt={source.label}
                  onError={() => setImageFailed(true)}
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: rotation % 180 ? size.height * zoom : '100%',
                    height: rotation % 180 ? size.width * zoom : '100%',
                    objectFit: 'contain',
                    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
                  }}
                />
              </Box>
            </Box>
          </>
        ) : (
          <Typography sx={{ my: 2 }}>{t('copilot.capture.downloadHint')}</Typography>
        ))}

      {fileUrl && (
        <Button component="a" href={fileUrl} download={source.label} sx={{ flexShrink: 0, alignSelf: 'flex-end' }}>
          {t('copilot.capture.download')}
        </Button>
      )}
    </Box>
  );
};

export const CaptureSourceViewer = ({
  caseId,
  source,
  onClose,
}: {
  caseId: number;
  source: TravelerCaptureSource;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle sx={{ overflowWrap: 'anywhere' }}>{source.label}</DialogTitle>
      <DialogContent>
        <CaptureSourceContent key={source.id} caseId={caseId} source={source} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
